import { Link } from "react-router-dom"
import {
  Building2, Users, ArrowRight, BookOpen, GraduationCap, FlaskConical,
  Languages, Palette, Wrench, AlertCircle, Sparkles,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { usePublicFaculties } from "../../hooks/usePublic"

const facultyIconMap: Record<string, any> = {
  SCI: FlaskConical,
  LAN: Languages,
  VTE: Wrench,
  ASS: Palette,
  EDU: BookOpen,
}

export default function Faculties() {
  const { data, isLoading } = usePublicFaculties()
  const faculties = data?.faculties || []

  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Faculties" }]}
        subtitle="Academic Faculties"
        title="Five Faculties. One Mission: Excellence in Teacher Education"
        description="The College's academic structure is organised into five faculties, each headed by a Dean and supported by dedicated departments offering NCE programmes accredited by the National Commission for Colleges of Education."
      />

      {/* Intro stats */}
      <section className="py-12 -mt-8">
        <div className="container mx-auto px-4">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-brand-800 to-brand-950 text-white">
            <CardContent className="pt-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="font-display text-3xl md:text-4xl font-bold text-gold-300">5</div>
                <div className="text-xs uppercase tracking-wider text-white/70 mt-1">Faculties</div>
              </div>
              <div>
                <div className="font-display text-3xl md:text-4xl font-bold text-gold-300">19</div>
                <div className="text-xs uppercase tracking-wider text-white/70 mt-1">Departments</div>
              </div>
              <div>
                <div className="font-display text-3xl md:text-4xl font-bold text-gold-300">35+</div>
                <div className="text-xs uppercase tracking-wider text-white/70 mt-1">Programmes</div>
              </div>
              <div>
                <div className="font-display text-3xl md:text-4xl font-bold text-gold-300">450+</div>
                <div className="text-xs uppercase tracking-wider text-white/70 mt-1">Academic Staff</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Faculties */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5].map(i => (
                <Card key={i} className="border-0 shadow-md">
                  <CardContent className="pt-6">
                    <div className="animate-pulse space-y-3">
                      <div className="w-14 h-14 bg-ink-200 dark:bg-ink-800 rounded-xl" />
                      <div className="h-5 bg-ink-200 dark:bg-ink-800 rounded w-2/3" />
                      <div className="h-4 bg-ink-200 dark:bg-ink-800 rounded w-full" />
                      <div className="h-4 bg-ink-200 dark:bg-ink-800 rounded w-3/4" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : faculties.length === 0 ? (
            <Card className="border-0 shadow-md">
              <CardContent className="py-16 text-center">
                <AlertCircle className="w-12 h-12 text-ink-400 mx-auto mb-3" />
                <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">Faculty Information Loading</h3>
                <p className="text-sm text-ink-500 dark:text-ink-400 max-w-md mx-auto">
                  Faculty details are being updated. Please check back shortly or contact the Academic Office for more information.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {faculties.map(f => {
                const Icon = facultyIconMap[f.code] || Building2
                return (
                  <Card key={f.id} className="card-lift border-0 shadow-md overflow-hidden group">
                    <div className="h-1.5 bg-gradient-to-r from-brand-600 to-gold-500" />
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-700 to-brand-900 grid place-items-center shadow-md">
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <Badge variant="outline">{f.code}</Badge>
                      </div>
                      <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">{f.name}</h3>
                      <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed mb-4">
                        {f.description || `The Faculty of ${f.name} is one of the five academic faculties of the College, offering accredited NCE programmes through its dedicated departments.`}
                      </p>
                      <div className="space-y-2 pt-4 border-t border-ink-100 dark:border-ink-800">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-ink-500 dark:text-ink-400 flex items-center gap-1.5">
                            <Users className="w-4 h-4" /> Departments
                          </span>
                          <span className="font-semibold text-ink-900 dark:text-ink-100">{f.dept_count || 0}</span>
                        </div>
                        {f.dean && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-ink-500 dark:text-ink-400 flex items-center gap-1.5">
                              <GraduationCap className="w-4 h-4" /> Dean
                            </span>
                            <span className="font-semibold text-ink-900 dark:text-ink-100 text-right">{f.dean}</span>
                          </div>
                        )}
                        {f.established && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-ink-500 dark:text-ink-400 flex items-center gap-1.5">
                              <Sparkles className="w-4 h-4" /> Established
                            </span>
                            <span className="font-semibold text-ink-900 dark:text-ink-100">{f.established}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* Programs link */}
      <section className="py-16 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/programs" className="group">
              <Card className="border-0 shadow-md hover:shadow-lg transition-shadow h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center">
                      <BookOpen className="w-6 h-6 text-brand-700 dark:text-brand-400" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-ink-400 group-hover:text-brand-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-ink-900 dark:text-ink-100 mb-2">Explore Programmes</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">
                    Discover the full range of NCE programmes offered across our faculties, including subject combinations, course outlines and duration.
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/admission-requirements" className="group">
              <Card className="border-0 shadow-md hover:shadow-lg transition-shadow h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-gold-50 dark:bg-gold-900/30 grid place-items-center">
                      <GraduationCap className="w-6 h-6 text-gold-700 dark:text-gold-400" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-ink-400 group-hover:text-gold-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-ink-900 dark:text-ink-100 mb-2">Admission Requirements</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">
                    Learn what it takes to join any of our faculties — entry requirements, required documents and the step-by-step application process.
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-14 h-14 rounded-xl bg-gold-500/20 grid place-items-center mx-auto mb-5">
              <Users className="w-7 h-7 text-gold-300" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Join Our Community</h2>
            <p className="text-white/75 mb-8 max-w-2xl mx-auto leading-relaxed">
              Whether you are a future student, a prospective staff member or a partner institution, there is a place for you at Yusuf Maitama Sule College of Education. Apply today and become part of a community dedicated to shaping Nigeria's future through teacher education.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/portal/register">
                <Button size="lg" className="bg-gold-500 hover:bg-gold-600 text-ink-950">
                  Apply for Admission <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/10">
                  Contact Admissions
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
